/**
 * Renderizado y edición de los DIEZ tipos de campo del contrato (§3.4).
 *
 * El conjunto es cerrado a propósito y pequeño: un tipo nuevo es un cambio del
 * genérico y pasa por revisión de arquitectura — es la excepción legítima y
 * declarada del §6.3, no una puerta libre. Por eso un tipo desconocido no se
 * «apaña»: se pinta como no soportado, visible, en vez de degradar en silencio.
 *
 * Las dos VÁLVULAS (`json` y `compuesto`) delegan en el widget declarado. Si el
 * widget no está registrado, el valor se muestra en crudo y en solo lectura:
 * **el visor transporta y muestra, nunca valida el significado.** Quien acepta o
 * rechaza es el kernel, y su código de error se pinta tal cual.
 */

import { widgetDe } from './registro.js'

/**
 * El conjunto CERRADO de tipos, en un solo sitio del front (R-10).
 *
 * La autoridad es el kernel (`kernel/mecanismos.py`), que lo valida al arrancar
 * y tiene un test que lo fija; esta es su única copia en el front, y quien la
 * necesite —tests incluidos— la importa en vez de reescribirla. Tres copias sin
 * vínculo mecánico entre ellas es el patrón de desincronización de siempre.
 *
 * Ampliar el conjunto es un cambio del genérico: pasa por revisión de
 * arquitectura, y toca este fichero **y** el del kernel.
 */
export const TIPOS_CAMPO = [
  'texto_corto',
  'texto_largo',
  'enum',
  'enum_multiple',
  'booleano',
  'fecha',
  'referencia',
  'lista_texto',
  'json',
  'compuesto',
]

const vacio = v => v === null || v === undefined || v === ''

function Texto({ valor }) {
  return <span className="mec-campo__texto">{vacio(valor) ? '—' : String(valor)}</span>
}

// --- Lectura ---------------------------------------------------------------

export function ValorCampo({ campo, valor }) {
  if (campo?.widget) {
    const Widget = widgetDe(campo.widget)
    if (Widget) return <Widget campo={campo} valor={valor} soloLectura />
  }
  switch (campo?.tipo) {
    case 'booleano':
      return <span className="mec-campo__texto">{valor ? 'Sí' : 'No'}</span>
    case 'enum_multiple':
    case 'lista_texto':
      return (
        <span className="mec-campo__etiquetas">
          {(Array.isArray(valor) ? valor : []).map(v => (
            <span key={String(v)} className="mec-etiqueta">{String(v)}</span>
          )) || '—'}
        </span>
      )
    case 'json':
    case 'compuesto':
      return (
        <pre className="mec-campo__json">
          {vacio(valor) ? '—' : JSON.stringify(valor, null, 2)}
        </pre>
      )
    default:
      return <Texto valor={valor} />
  }
}

// --- Edición ---------------------------------------------------------------

/**
 * GEN-GR-004a — qué significa el valor elegido y cuándo se usa. El texto lo
 * declara el descriptor (`ayuda_valores`); aquí solo se pinta, para cada valor
 * elegido que tenga ayuda. Sin valor elegido o sin ayuda, no se pinta nada.
 */
export function AyudaValor({ campo, valor }) {
  const ayudas = campo?.ayuda_valores
  if (!ayudas) return null
  const elegidos = (Array.isArray(valor) ? valor : [valor]).filter(v => !vacio(v) && ayudas[v])
  if (!elegidos.length) return null
  return (
    <div className="mec-ayuda-valor" role="note" aria-label={`Qué significa ${campo.etiqueta}`}>
      {elegidos.map(v => (
        <p key={String(v)}>
          <strong>{String(v)}</strong>: {ayudas[v].significado}
          <br />
          <em>Cuándo usarlo:</em> {ayudas[v].cuando_usar}
        </p>
      ))}
    </div>
  )
}

export function EditorCampo({ campo, valor, onChange, disabled, invalido }) {
  const comun = {
    id: `campo-${campo.id}`,
    disabled,
    'aria-label': campo.etiqueta,
    'aria-invalid': invalido ? 'true' : undefined,
  }

  if (campo.widget) {
    const Widget = widgetDe(campo.widget)
    if (Widget) return <Widget campo={campo} valor={valor} onChange={onChange} />
  }

  switch (campo.tipo) {
    case 'texto_largo':
      return (
        <textarea
          {...comun}
          className="mec-input mec-input--area"
          rows={5}
          value={valor ?? ''}
          onChange={e => onChange(e.target.value)}
        />
      )
    case 'enum':
      return (
        <select
          {...comun}
          className="mec-input"
          value={valor ?? ''}
          onChange={e => onChange(e.target.value)}
        >
          <option value="">—</option>
          {(campo.opciones ?? []).map(o => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
      )
    case 'enum_multiple':
      return (
        <select
          {...comun}
          className="mec-input"
          multiple
          value={Array.isArray(valor) ? valor : []}
          onChange={e => onChange(Array.from(e.target.selectedOptions, o => o.value))}
        >
          {(campo.opciones ?? []).map(o => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
      )
    case 'booleano':
      return (
        <input
          {...comun}
          className="mec-input mec-input--check"
          type="checkbox"
          checked={Boolean(valor)}
          onChange={e => onChange(e.target.checked)}
        />
      )
    case 'fecha':
      return (
        <input
          {...comun}
          className="mec-input"
          type="date"
          value={valor ?? ''}
          onChange={e => onChange(e.target.value)}
        />
      )
    case 'lista_texto':
      return (
        <textarea
          {...comun}
          className="mec-input mec-input--area"
          rows={4}
          value={(Array.isArray(valor) ? valor : []).join('\n')}
          onChange={e => onChange(e.target.value.split('\n').filter(Boolean))}
        />
      )
    case 'json':
    case 'compuesto':
      // Válvula sin widget: viaja en crudo y lo valida el kernel. Se transporta
      // el TEXTO tal cual — no se intenta parsear ni «arreglar» aquí.
      return (
        <textarea
          {...comun}
          className="mec-input mec-input--area mec-input--mono"
          rows={6}
          value={typeof valor === 'string' ? valor : JSON.stringify(valor ?? '', null, 2)}
          onChange={e => onChange(e.target.value)}
        />
      )
    case 'texto_corto':
    case 'referencia':
      return (
        <input
          {...comun}
          className="mec-input"
          type="text"
          value={valor ?? ''}
          onChange={e => onChange(e.target.value)}
        />
      )
    default:
      return (
        <span className="mec-campo__no-soportado" role="alert">
          Tipo de campo no soportado por esta versión del visor: {campo.tipo}
        </span>
      )
  }
}
